import { compress, decompress } from 'lz-string';
import { useSelector } from 'react-redux';
import { PersonRoleInitApp, ReduxState } from 'types';
import { useDeepCompareMemo } from 'use-deep-compare';
import { IDENTIFIER_FIRST_LOGIN } from '../../consts/consts';
import { KeycloakAuthContextValue } from '../api/KeycloakAuthContext';
import { isKeycloakEnabled } from '../api/keycloakClient';
import { publishAuthChanged, setLastKnownMeId } from '../api/sessionBroadcast';
import config from '../util/config';

const getLocalStorageFullKey = (auth0SubId, currentProxyPerson, orgId, key) => {
  return (
    '@@confirm@@::' +
    process.env.REACT_APP_VERSION +
    '::' +
    compress(
      'user::' +
        auth0SubId +
        (currentProxyPerson?.email
          ? '::proxy::' + currentProxyPerson.email
          : '') +
        (orgId ? '::org::' + orgId : '') +
        '::' +
        key
    )
  );
};

const LOGGED_OUT_LOCAL_STORAGE_KEY = 'logged-out-locale';

const getLoggedOutLocaleKey = () => {
  return getLocalStorageFullKey('', '', '', LOGGED_OUT_LOCAL_STORAGE_KEY);
};

export const setLoggedOutLocaleInLocalStorage = (value) => {
  return localStorage.setItem(getLoggedOutLocaleKey(), value);
};

export const getLoggedOutLocaleFromLocalStorage = () => {
  return localStorage.getItem(getLoggedOutLocaleKey());
};

export const reverseLocalStorageFullKey = (fullKey) => {
  const compressedKey = fullKey.split('::').slice(-1)[0];
  let decompressedKey = compressedKey;
  try {
    decompressedKey = decompress(compressedKey);
  } catch (e) {
    console.log(e);
  }
  return decompressedKey;
};

// serialize and save object to local storage for given auth0 user
export const removeUserLocalStorage = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  key
) => {
  const fullKey = getLocalStorageFullKey(
    auth0SubId,
    currentProxyPerson,
    orgId,
    key
  );
  return localStorage.removeItem(fullKey);
};

// serialize and save object to local storage for given auth0 user
export const setUserLocalStorage = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  key,
  value,
  // for expiration, we encode it differently per
  // https://www.sohamkamani.com/blog/javascript-localstorage-with-ttl-expiry/
  ttlInMilliseconds = null,
  // generate and store UTC timestamp in MS per
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTime
  withTimestamp = false
) => {
  const fullKey = getLocalStorageFullKey(
    auth0SubId,
    currentProxyPerson,
    orgId,
    key
  );

  if (value) {
    try {
      const valueToSave =
        ttlInMilliseconds || withTimestamp
          ? {
              value,
              // @ts-expect-error
              expiry: new Date().getTime() + ttlInMilliseconds,
              timestamp: new Date().getTime(),
            }
          : value;
      return localStorage.setItem(
        fullKey,
        compress(JSON.stringify(valueToSave))
      );
    } catch (e) {
      // quota exceeded, so clear local storage for now
      // If this happens it will mess up short sessions management (if in use).
      console.warn('quota exceeded, clear local storage for now', e);
      localStorage.clear();
    }
  } else {
    return localStorage.removeItem(fullKey);
  }
};

// fetch and deserialize object from local storage for given auth0 user
export const getUserLocalStorage = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  key,
  hasExpiry = false,
  withTimestamp = false
) => {
  const fullKey = getLocalStorageFullKey(
    auth0SubId,
    currentProxyPerson,
    orgId,
    key
  );

  const output = localStorage.getItem(fullKey);

  // parse and decompress, but if failure, just return undefined
  try {
    // no item to return
    if (!output) {
      return withTimestamp ? [null, null] : null;
    }

    const parsedOutput = JSON.parse(decompress(output));

    if (hasExpiry || withTimestamp) {
      // if expired, clear out storage and return null
      if (hasExpiry && new Date().getTime() > parsedOutput.expiry) {
        localStorage.removeItem(fullKey);
        return withTimestamp ? [null, null] : null;
      }

      if (withTimestamp) {
        return [parsedOutput.value, parsedOutput.timestamp];
      }

      return parsedOutput.value;
    } else {
      return parsedOutput;
    }
  } catch (err) {
    console.error(
      'Error fetching from local storage ' + key + ': ' + JSON.stringify(err)
    );
    return undefined;
  }
};

// default universal params for checking if we should show the user their various
// accounts to select for logging in
const DEFAULT_AUTH0_SUB_ID = 0;
const DEFAULT_PROXY_PERSON = null;
const DEFAULT_ORG_ID = null;
const HAS_SEEN_SELECT_ACCOUNT_PROMPT_KEY = 'has-seen-select-account-prompt';
export const SHOULD_SHOW_FORCED_AUTH_MODAL_KEY =
  'should-show-forced-auth-modal';

export const getHasSeenSelectAccountPrompt = () => {
  return getUserLocalStorage(
    DEFAULT_AUTH0_SUB_ID,
    DEFAULT_PROXY_PERSON,
    DEFAULT_ORG_ID,
    HAS_SEEN_SELECT_ACCOUNT_PROMPT_KEY
  );
};

export const setHasSeenSelectAccountPrompt = (value) => {
  setUserLocalStorage(
    DEFAULT_AUTH0_SUB_ID,
    DEFAULT_PROXY_PERSON,
    DEFAULT_ORG_ID,
    HAS_SEEN_SELECT_ACCOUNT_PROMPT_KEY,
    value
  );
};

export const getShouldShowForcedAuthModal = () => {
  return getUserLocalStorage(
    DEFAULT_AUTH0_SUB_ID,
    DEFAULT_PROXY_PERSON,
    DEFAULT_ORG_ID,
    SHOULD_SHOW_FORCED_AUTH_MODAL_KEY
  );
};

export const setShouldShowForcedAuthModal = (value) => {
  setUserLocalStorage(
    DEFAULT_AUTH0_SUB_ID,
    DEFAULT_PROXY_PERSON,
    DEFAULT_ORG_ID,
    SHOULD_SHOW_FORCED_AUTH_MODAL_KEY,
    value
  );
};

export const getUserRole = (authUser) => {
  return getUserIsSuperAdmin(authUser) ? 'admin' : null;
};

export const getUserIsSuperAdmin = (authUser) => {
  // TODO: figure out role mappings in KC
  return (
    authUser &&
    authUser['https://app.confirmhr.com/roles'] &&
    authUser['https://app.confirmhr.com/roles'].includes('admin')
  );
};

export const useIsSuperAdmin = () => {
  const isSuperAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_super_admin ?? false
  );
  return isSuperAdmin;
};

export const useIsSuperAdminNonImpersonating = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const isImpersonating = useIsImpersonating();
  return isSuperAdmin && !isImpersonating;
};

export const useIsImpersonating = () => {
  const isSuperAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_impersonating ?? false
  );
  return isSuperAdmin;
};

export const useIsImpersonatinPerson = (): boolean => {
  const isImpersonatingPerson = useSelector<ReduxState, boolean>(
    (state) => !!state?.currentProxyPerson
  );
  return isImpersonatingPerson;
};

export const userHasCompletedWelcome = (myConfigs) => {
  return myConfigs?.welcome_completed_at ? true : false;
};

// `logout` is whichever provider's logout useAuthProvider() handed the
// caller - Auth0's or Keycloak's - both take the same { returnTo, localOnly }
// options, which is the shape KeycloakAuthContextValue['logout'] declares.
export const logOutAndCleanUp = (
  logout: KeycloakAuthContextValue['logout']
) => {
  // don't ask for which account when auto-refreshing session and checking if still
  // logged into third party used to authenticate
  setHasSeenSelectAccountPrompt(true);

  // Log out fully.
  // returnTo so Auth0 redirects to the tenant's default logout URL
  // (the marketing site); everywhere else land on the app root so the
  // login page comes straight back up.
  // @ts-expect-error
  if (config.isProduction() || config.isUat()) {
    logout();
  } else {
    if (isKeycloakEnabled()) {
      // Land back on the identifier-first login page (see keycloakClient.ts).
      logout({
        returnTo: window.location.origin + IDENTIFIER_FIRST_LOGIN.path,
      });
    } else {
      logout({ returnTo: window.location.origin });
    }
  }

  // clear local storage (we do after logout to ensure no longering storage happens during logout)
  localStorage.clear();

  // Notify any other tabs that auth has changed. We re-clear the
  // last-known-me-id key after the broadcast in case any handler races to
  // re-populate it from a stale Redux subscription.
  setLastKnownMeId(null);
  publishAuthChanged();
};

const UNSAVED_CHANGES_TTL_IN_MS = 24 * 3600 * 1000; // 24 hours

export const storeUnsavedChanges = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  name,
  value
) => {
  return setUserLocalStorage(
    auth0SubId,
    currentProxyPerson,
    orgId,
    name,
    value,
    // @ts-expect-error
    UNSAVED_CHANGES_TTL_IN_MS,
    true
  );
};

export const clearUnsavedChanges = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  name
) => {
  return removeUserLocalStorage(auth0SubId, currentProxyPerson, orgId, name);
};

export const getUnsavedChanges = (
  auth0SubId,
  currentProxyPerson,
  orgId,
  name
) => {
  return getUserLocalStorage(
    auth0SubId,
    currentProxyPerson,
    orgId,
    name,
    true,
    true
  );
};

export const useIsHRAdmin = (): boolean => {
  const isHrAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_hrbp ?? false
  );
  return isHrAdmin;
};

export const useIsOrgSystemAdmin = (): boolean => {
  const isOrgSystemAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_system_admin ?? false
  );
  return isOrgSystemAdmin;
};

export const useIsOneOnOneAdmin = (): boolean => {
  const isOneOnOneAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_one_on_one_admin ?? false
  );
  return isOneOnOneAdmin;
};

export const useIsPromotionPacketAdmin = (): boolean => {
  const isPromotionPacketAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_promotion_packet_admin ?? false
  );
  return isPromotionPacketAdmin;
};

export const useIsObjectivesAdmin = (): boolean => {
  const isObjectivesAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_objectives_admin ?? false
  );
  return isObjectivesAdmin;
};

export const useIsAIAdmin = (): boolean => {
  const isAIAdmin = useSelector<ReduxState, boolean>(
    (state) => state?.currentOrganization?.is_ai_admin ?? false
  );
  return isAIAdmin;
};

// Mirrors Person.has_active_direct_reports() (CONF-434) - a manager with
// active direct reports gets the same custom-survey-management access as an
// HRBP. This is a simpler frontend approximation (just "does me.direct_reports
// have anything in it") rather than the backend's full campaign/multi-person
// logic - fine here since this only gates nav/route visibility, not the
// actual API permission check.
export const useHasDirectReports = (): boolean => {
  const hasDirectReports = useSelector<ReduxState, boolean>(
    (state) => (state?.me?.direct_reports?.length ?? 0) > 0
  );
  return hasDirectReports;
};

export type DevelopmentPlanAdminConfig = {
  isDevelopmentPlanAdmin: boolean;
  targetPlansIds: string[];
};

export const useDevelopmentPlanAdmin = (): DevelopmentPlanAdminConfig => {
  const developmentPlanAdminRole = useSelector<
    ReduxState,
    PersonRoleInitApp | undefined
  >((state) =>
    state?.roles?.find((role) => role.role === 'DEVELOPMENT_PLAN_ADMIN')
  );

  const result = useDeepCompareMemo(
    () => ({
      isDevelopmentPlanAdmin: !!developmentPlanAdminRole,
      targetPlansIds:
        developmentPlanAdminRole?.config?.target_development_plans ?? [],
    }),
    [developmentPlanAdminRole]
  );

  return result;
};

// Whether the current person is a calibration view editor of the campaign,
// i.e. may create calibration views for it (and delete the ones they own or
// administer). The role is scoped to the campaigns in its config (none
// configured = all), so without a campaign id this is always false.
export const useIsCalibrationViewEditor = (campaignId?: number): boolean => {
  const calibrationViewEditorRole = useSelector<
    ReduxState,
    PersonRoleInitApp | undefined
  >((state) =>
    state?.roles?.find((role) => role.role === 'CALIBRATION_VIEW_EDITOR')
  );

  if (!calibrationViewEditorRole || campaignId === undefined) {
    return false;
  }

  const targetCampaigns =
    calibrationViewEditorRole.config?.target_campaigns ?? [];
  // config is unvalidated JSON: tolerate ids saved as strings
  return (
    targetCampaigns.length === 0 ||
    targetCampaigns.some((id) => Number(id) === campaignId)
  );
};

export const useIsCalibrationViewAdmin = (): boolean => {
  const hrAdminRole = useSelector<ReduxState, PersonRoleInitApp | undefined>(
    (state) => state?.roles?.find((role) => role.role === 'HR_ADMIN')
  );
  return !!hrAdminRole?.config?.calibration_view_admin;
};
